import {
  compactInteger,
  formatNumber as humanizeFormatNumber,
  intComma as humanizeIntComma,
  titleCase,
  toFixed,
} from 'humanize-plus';
import { isNil, take, takeRight } from 'lodash';
import { AddressFieldFormatter } from '~domain/formatters/address-field-formatter';
import Address from '~domain/model/application/home/address';

/**
 * Compact a string field into a more shorter form. From the
 * input `000210185aca9762f3906832b3ac983510862a04f2b6ee602619958ddccb5823`,
 * the resulting output of this function will be
 * `000210185aca9762f3906832b3ac983510862a...dccb5823`
 *
 * @param input The input to shorten
 */
export function compactString(input: number | string, first: number = 4, last: number = 2) {
  const id = input.toString();

  return `${take(id, first)}...${takeRight(id, last)}`;
}

export function capitalizeFirstLetter(value: string) {
  if (isNil(value)) return;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

export function compactCount(input: number) {
  return compactInteger(input, 2);
}

export function decapitalizeFirstLetter(value: string) {
  if (isNil(value)) return;
  return value.charAt(0).toLowerCase() + value.slice(1);
}

/**
 * Convert camelCaseInput to snake-case-input
 * @param input string in camel case format
 */
export function formatCamelCaseToSnakeCase(input: string) {
  if (input == null) return input;

  return input.replace(/[A-Z]/g, (letter) => `-${letter.toLowerCase()}`);
}

/**
 * Format number to amount based on the language
 * @param input number to format
 * @param decimals number of decimals
 * @param languageId language
 * @ouput EN 9999 -> $ 9,999
 * @ouput FR 9999 -> 9 999 $
 */
export function formatCurrencyAmount(
  input: number,
  decimals: number,
  languageId: string,
  withDollarSign: boolean = true,
) {
  // Format number with english format
  const humanizedAmount: string = humanizeIntComma(input, decimals);

  // If current language is french, replace commas by spaces
  if (languageId === 'fr') {
    let formattedValue: string = humanizedAmount.replace(new RegExp(',', 'g'), ' ');
    // If dollar sign is requested, Place the dollar sign at the end
    if (withDollarSign) formattedValue = formattedValue.concat(' $');
    return formattedValue;
  }

  // If no dollar sign, simply return
  if (!withDollarSign) return humanizedAmount;
  // For other language, place the dollar sign at the beginning
  return `$ ${humanizedAmount}`;
}

export function formatNumber(input: number) {
  return humanizeFormatNumber(input);
}

export function formatTitleCase(input: string) {
  if (!input) return input;
  return titleCase(input.toLowerCase());
}

// 123456789 => "123,456,789"
export function intComma(input: number) {
  return humanizeIntComma(input);
}

export function formatPercentage(input: number) {
  const value = input < 1.0 ? input * 100.0 : input;

  return `${toFixed(value, 2)} %`;
}

export function formatVariation(input: number) {
  if (input === 0) {
    return '0.00';
  }

  const sign = input < 0 ? '-' : '+';
  const value = toFixed(input, 2);

  return `${sign}${value}`;
}

// Format error message
export function formatStringWithPrefix(prefix: string, value: string): string {
  // If no label is has been passed, simply return the error
  if (!prefix || prefix === '') return value;
  return `${prefix} - ${value}`;
}

// Convert single digit number to two digits. E.g. 1 is converted to 01
export function formatNumberToTwoDigits(value: number): string {
  return value.toLocaleString(undefined, { minimumIntegerDigits: 2 });
}

// Convert string to readable phone number
// e.g. 151412345678 returns +1 (514) 123-45678
export function formatPhoneNumber(value: string, spaces: 'no-break' | 'normal' = 'normal'): string {
  value = value || '';

  // \xa0 is a non-breaking space
  const space = spaces === 'no-break' ? '\xa0' : ' ';

  let formattedValue = '';

  // If input already includes +, remove it before formatting
  if (value[0] === '+') {
    value = value.slice(1);
  }

  // If phone number includes country indicator, add it first
  if (value.length > 10) {
    const countryIndicator = value[0];
    formattedValue += `+${countryIndicator}${space}`;
    value = value.slice(1);
  }

  // Format next 10 digits
  formattedValue += `(${value.slice(0, 3)})${space}${value.slice(3, 6)}-${value.slice(6)}`;

  return formattedValue;
}

// Remove accents from input
// Ref: stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
export function removeDiacritics(input: string): string {
  return input?.normalize('NFD')?.replace(/[\u0300-\u036f]/g, '');
}

// Remove commas from number
export function unFormatCommaNumber(value: string): string {
  if (value) {
    return value.replace(/,/g, '');
  }

  return value;
}

// Mainly used for fortus warning messages - remove punctuation and invisible characters
export function sanitizeString(input: string): string {
  if (isNil(input)) return input;
  const lowerCaseInput = input.toLowerCase().trim();
  const noInvisibleCharacters = replaceInvisibleCharacters(lowerCaseInput);
  const noInnerSpaces = trimInnerSpaces(trimInnerSpaces(noInvisibleCharacters));
  const noDiacritics = removeDiacritics(noInnerSpaces);
  return removePunctuation(noDiacritics);
}

// Remove special characters
// From https://www.markodevcic.com/post/zero_width_space/
function replaceInvisibleCharacters(input: string): string {
  return input.replace(/[\s\u200B-\u200D\uFEFF]/g, ' ');
}

// Deletes punctuation from string
function removePunctuation(input: string): string {
  return input.replace(/[.,\/#!$%\^&\*;:{}=\-_`~()]/g, '');
}

// long inner spaces in string get replaced with a single space
function trimInnerSpaces(input: string) {
  return input.replace(/  +/g, ' ');
}

// Format address object into a readable one line string
export function formatAddress2String(
  address: Omit<Address, 'coordinates'>,
  options: {
    ignoreUnit?: boolean;
    fieldOverride?: Partial<Record<keyof Address, AddressFieldFormatter>>;
  } = {
    ignoreUnit: false,
  },
): string {
  if (!address) {
    return '';
  }

  // Shallow copy so we can modify the address as needed
  const addressCopy = {
    ...address,
  };

  const { fieldOverride } = options;
  if (!isNil(fieldOverride)) {
    Object.keys(fieldOverride).forEach((addressKey: string) => {
      addressCopy[addressKey] = fieldOverride[addressKey](addressCopy[addressKey]);
    });
  }

  let formattedAddress = `${addressCopy.streetNumber} ${addressCopy.streetName}, ${addressCopy.city}, ${
    addressCopy.province
  } ${addressCopy.postalCode}, ${capitalizeFirstLetter(addressCopy.country)}`;

  if (!options.ignoreUnit && !isNil(addressCopy.unit)) {
    formattedAddress = `${addressCopy.unit} - ${formattedAddress}`;
  }

  return formattedAddress;
}

export function removeSpaces(input: string): string {
  return input?.replace(/\s/g, '');
}
